import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllWarehouses,
  createWarehouse,
  updateWarehouse,
  deleteWarehouse,
  getAllCountries,
} from "../../redux/actions";
import { LoadingModal } from "../../components/modal/loading";
import {
  CreateWarehouseModal,
  EditWarehouseModal,
  DeleteConfirmModal,
} from "../../components/shipping/ShippingModals";
import { WarehouseDetailModal } from "../../components/shipping/DetailModals";

export const Warehouse = () => {
  const dispatch = useDispatch();
  const { warehouses, countries, loading } = useSelector((s) => s.shipping);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    dispatch(getAllWarehouses());
    dispatch(getAllCountries());
  }, [dispatch]);

  const handleCreate = (form) => {
    dispatch(createWarehouse(form));
    setCreateOpen(false);
  };

  const handleUpdate = (form) => {
    dispatch(updateWarehouse(selected.id, form));
    setEditOpen(false);
    setSelected(null);
  };

  const handleDelete = () => {
    dispatch(deleteWarehouse(selected.id));
    setDeleteOpen(false);
    setSelected(null);
  };

  return (
    <div className="space-y-6">
      {loading && <LoadingModal text="Loading..." />}

      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Warehouses</h1>
        <button
          onClick={() => setCreateOpen(true)}
          className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
        >
          Add Warehouse
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                #
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Country
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                City
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Contact
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Shipping Mark
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {warehouses.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-8 text-center text-slate-400 text-sm"
                >
                  No warehouses found. Add one to get started.
                </td>
              </tr>
            ) : (
              warehouses.map((w, i) => (
                <tr key={w.id} className="hover:bg-slate-50 transition">
                  <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {w.name}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {w.country?.name}{" "}
                    <span className="text-xs text-slate-400">
                      ({w.country?.code})
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{w.city}</td>
                  <td className="px-4 py-3 text-slate-600">
                    <div>{w.contactPerson}</div>
                    <div className="text-xs text-slate-400">{w.phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    {w.shippingMark && (
                      <span className="rounded-lg bg-slate-900 px-2.5 py-1 text-xs font-bold text-white tracking-wider">
                        {w.shippingMark}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => {
                        setSelected(w);
                        setDetailOpen(true);
                      }}
                      className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                      View
                    </button>
                    <button
                      onClick={() => {
                        setSelected(w);
                        setEditOpen(true);
                      }}
                      className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setSelected(w);
                        setDeleteOpen(true);
                      }}
                      className="rounded-lg bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-100"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CreateWarehouseModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        countries={countries}
      />
      <EditWarehouseModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        onUpdate={handleUpdate}
        warehouse={selected}
        countries={countries}
      />
      <WarehouseDetailModal
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelected(null);
        }}
        warehouse={selected}
      />
      <DeleteConfirmModal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        onConfirm={handleDelete}
        title="Delete Warehouse"
        message={`Are you sure you want to delete "${selected?.name}"? This action cannot be undone.`}
      />
    </div>
  );
};
